import { Fragment, useEffect, useState } from 'react';
import { api, VOICES, voiceLabel, isCloudVoice, type Asset, type Brand, type Voice, type VoiceId, type Workspace, SOCIAL_PLATFORMS, type SocialPlatform, SHOW_LANGUAGES } from '../lib/api';
import { Thumb } from './AssetGrid';

/**
 * The two colours and the word every render honours.
 *
 * Deliberately shows the result rather than describing it. A hex field tells you
 * nothing about whether a thumbnail will read at the 210 pixels a phone gives
 * it, so the preview here is laid out like the real one — same ground, same
 * accent, same relative weight — and the text colour is computed the same way
 * the render plane computes it, by measured contrast.
 *
 * That last part is the point of the whole screen: a creator cannot choose an
 * unreadable combination, because they do not choose the text colour at all.
 */

const DEFAULT_ACCENT = '#ff3c41';
const DEFAULT_GROUND = '#0e0f12';

function rgb(hex: string): [number, number, number] {
  const s = hex.replace('#', '');
  return [
    parseInt(s.slice(0, 2), 16),
    parseInt(s.slice(2, 4), 16),
    parseInt(s.slice(4, 6), 16),
  ];
}

/** WCAG relative luminance: sRGB linearised, then weighted. */
function luminance(hex: string): number {
  const [r, g, b] = rgb(hex).map((v) => {
    const x = v / 255;
    return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a: string, b: string): number {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (l1 + 0.05) / (l2 + 0.05);
}

const DARK_INK = '#111114';
const LIGHT_INK = '#f3f3f5';

/**
 * The same decision the render plane makes, for the same reason: this is the
 * one colour a creator must not be able to get wrong, so it is measured rather
 * than offered. Mirrored here only to preview it — palette.py remains the
 * implementation that reaches a frame.
 */
function inkFor(ground: string): string {
  return contrast(DARK_INK, ground) >= contrast(LIGHT_INK, ground) ? DARK_INK : LIGHT_INK;
}

function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = rgb(a);
  const [br, bg, bb] = rgb(b);
  const c = (x: number, y: number) => Math.round(x + (y - x) * t).toString(16).padStart(2, '0');
  return `#${c(ar, br)}${c(ag, bg)}${c(ab, bb)}`;
}

/** The accent used as TEXT on the ground — a third case, and the one that reads
 *  as a smudge if it is left alone (a yellow brand on a cream ground). */
function accentTextFor(accent: string, ground: string): string {
  const ink = inkFor(ground);
  for (const t of [0, 0.2, 0.4, 0.6, 0.8]) {
    const candidate = mix(accent, ink, t);
    if (contrast(candidate, ground) >= 4.5) return candidate;
  }
  return ink;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

export function BrandKit({ brand, onSaved, workspace }: { brand: Brand | null; onSaved: (b: Brand) => void; workspace?: Workspace | null }) {
  const [accent, setAccent] = useState(DEFAULT_ACCENT);
  const [ground, setGround] = useState(DEFAULT_GROUND);
  const [wordmark, setWordmark] = useState('');
  // 190: how the show's pictures look; in front of every picture prompt made on it.
  const [style, setStyle] = useState('');
  // The kit bench (190): a brief in, a proposed kit into these fields; Save keeps it.
  const [designBrief, setDesignBrief] = useState('');
  const [designLogo, setDesignLogo] = useState(false);
  const [designing, setDesigning] = useState(false);
  const [designed, setDesigned] = useState<string | null>(null);
  const [language, setLanguage] = useState('');
  const [voices, setVoices] = useState<Record<string, VoiceId>>({});
  // The cast's faces: name to a picture in the library (078).
  const [portraits, setPortraits] = useState<Record<string, string>>({});
  // Who they are (080): the script writer is told, so Niki opens as Niki.
  const [bios, setBios] = useState<Record<string, string>>({});
  const [handles, setHandles] = useState<Partial<Record<SocialPlatform, string>>>({});
  const [bio, setBio] = useState('');
  const [avatarId, setAvatarId] = useState('');
  const [logos, setLogos] = useState<string[]>([]);
  const [pictures, setPictures] = useState<Asset[]>([]);
  // The catalogue, fetched once: local, cloud (the accents), directed. Until
  // it arrives — or if the cloud tier is down — the four local voices are
  // the list, which is what the picker always showed.
  const [voiceList, setVoiceList] = useState<Voice[]>([...VOICES.map((v) => ({
    id: v.id, label: v.label, tier: 'local' as const, language: v.id.slice(0, 5).replace('_', '-'), accent: v.hint,
  }))]);
  const [playing, setPlaying] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    api.voices().then((list) => { if (live && list.length) setVoiceList(list); }).catch(() => {});
    return () => { live = false; };
  }, []);
  // Grouped for the select: local first, then Venice's models (one group per
  // model; Kokoro per accent), then each Google accent, then the directed
  // tier. An optgroup per group is what makes three hundred voices pickable.
  const groups = (() => {
    const out = new Map<string, Voice[]>();
    for (const v of voiceList) {
      const key = v.tier === 'local' ? 'In the render image'
        : v.tier === 'directed' ? 'Directed — reads the way you tell it'
        : v.tier === 'venice' ? (v.accent === v.hint ? `Venice · ${v.accent}` : `Venice · ${v.hint} · ${v.accent}`)
        : `${v.accent} · ${v.language}`;
      out.set(key, [...(out.get(key) ?? []), v]);
    }
    return [...out.entries()];
  })();
  async function preview(id: string) {
    setPlaying(id);
    try {
      const url = await api.voiceSample(id);
      if (!url) { setPlaying(null); return; }
      const a = new Audio(url);
      a.onended = () => { setPlaying(null); URL.revokeObjectURL(url); };
      await a.play();
    } catch { setPlaying(null); }
  }
  const [known, setKnown] = useState<string[]>([]);
  const [adding, setAdding] = useState('');
  // Each panel saves its own fields, so a saved / failed note sits in the
  // footer of the panel it belongs to.
  type Panel = 'look' | 'cast';
  const [busy, setBusy] = useState<Panel | null>(null);
  const [error, setError] = useState<{ at: Panel; message: string } | null>(null);
  const [saved, setSaved] = useState<Panel | null>(null);

  useEffect(() => {
    setAccent(brand?.accent_hex ?? DEFAULT_ACCENT);
    setGround(brand?.ground_hex ?? DEFAULT_GROUND);
    setWordmark(brand?.wordmark ?? '');
    setStyle(brand?.style ?? '');
    setLanguage(brand?.language ?? '');
    setVoices(brand?.voices ?? {});
    setPortraits(brand?.portraits ?? {});
    setBios(brand?.bios ?? {});
    setHandles(brand?.handles ?? {});
    setBio(brand?.bio ?? '');
    setLogos(brand?.logos ?? []);
    setSaved(null);
    setError(null);
  }, [brand?.id, brand?.accent_hex, brand?.ground_hex, brand?.wordmark, brand?.style, brand?.voices, brand?.portraits, brand?.bios, brand?.handles, brand?.logos]);

  // Only READY pictures: one still promoting has no key for the renderer to
  // draw, and the API refuses it anyway.
  useEffect(() => {
    if (!brand?.id) { setPictures([]); return; }
    let live = true;
    api.assets(brand.id)
      .then((all) => { if (live) setPictures(all.filter((a) => a.kind === 'image' && a.status === 'ready')); })
      .catch(() => { if (live) setPictures([]); });
    return () => { live = false; };
  }, [brand?.id]);

  // The speaker names this brand's scripts actually use. A cast is keyed on the
  // name, so casting one that appears in no script is a setting that does
  // nothing and says nothing — showing the real names is what makes it
  // checkable. A brand with no cards yet simply has none to offer, and the
  // field below still takes a typed name.
  useEffect(() => {
    if (!brand?.id) { setKnown([]); return; }
    let live = true;
    api.brandSpeakers(brand.id)
      .then((r) => { if (live) setKnown(r); })
      .catch(() => { if (live) setKnown([]); });
    return () => { live = false; };
  }, [brand?.id]);

  if (!brand) return <p className="hint">Choose a brand to give it a kit.</p>;

  const valid = HEX.test(accent) && HEX.test(ground);
  const ink = inkFor(ground);
  const accentText = accentTextFor(accent, ground);
  const dim = mix(ink, ground, 0.4);
  const mark = wordmark.trim() || brand.name;

  async function save(panel: Panel) {
    setBusy(panel); setError(null); setSaved(null);
    try {
      const next = await api.saveBrandKit(brand!.id, panel === 'look'
        ? {
            accentHex: accent,
            groundHex: ground,
            wordmark: wordmark.trim() === '' ? null : wordmark.trim(),
            style: style.trim() === '' ? null : style.trim(),
            language: language || null,
          }
        : {
            voices: Object.keys(voices).length ? voices : null,
            portraits: Object.keys(portraits).length ? portraits : null,
            bios: Object.fromEntries(Object.entries(bios).filter(([, b]) => b.trim())),
            handles: Object.fromEntries(Object.entries(handles).filter(([, h]) => (h ?? '').trim())),
            logos,
            bio: bio.trim() || null,
            avatarAssetId: avatarId || null,
          });
      onSaved(next);
      setSaved(panel);
    } catch (e) {
      setError({ at: panel, message: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <h2>Brand kit</h2>
      <p className="lede">
        What every video on this brand shares: its look, its talent, and how a new card starts.
        Nothing here reaches a video that has already been made.
      </p>

      <section className="kit" aria-labelledby="kit-look">
        <header className="kit-hd">
          <h3 id="kit-look">Look</h3>
          <p>
            Every thumbnail, panel and caption uses these two colours. You do not pick the text
            colour — it is worked out from your ground so a headline always reads, at the size a
            phone actually shows it.
          </p>
        </header>
        <form className="row wrap" style={{ gap: 8, alignItems: 'center', padding: '0 0 10px' }} onSubmit={(e) => {
          e.preventDefault();
          if (designBrief.trim().length < 5 || !brand) return;
          setDesigning(true); setDesigned(null); setError(null);
          api.designKit(brand.id, designBrief.trim(), designLogo)
            .then((r) => {
              setGround(r.proposal.groundHex); setAccent(r.proposal.accentHex); setWordmark(r.proposal.wordmark); setStyle(r.proposal.style);
              setDesigned([r.proposal.why, r.logoAssetId ? 'The logo is in the library under Logos — tick it under Talent & marks once it is ready.' : null, r.detail ?? null].filter(Boolean).join(' ') || 'Proposed — save the look to keep it.');
            })
            .catch((e) => setError({ at: 'look', message: (e as Error).message }))
            .finally(() => setDesigning(false));
        }}>
          <input value={designBrief} maxLength={600} onChange={(e) => setDesignBrief(e.target.value)} style={{ flex: 1, minWidth: 260 }}
                 placeholder="Design the kit from a brief — an anime folk tale in an Asian village, morning light" />
          <label className="row" style={{ gap: 4, alignItems: 'center' }}><input type="checkbox" checked={designLogo} onChange={(e) => setDesignLogo(e.target.checked)} /> and a logo</label>
          <button className="btn" type="submit" disabled={designing || designBrief.trim().length < 5}>{designing ? 'Designing…' : 'Design it'}</button>
          {designed && <span className="ok" style={{ flexBasis: '100%' }}>{designed}</span>}
        </form>
        <div className="kit-bd two">
          <div className="kitfields">
            <label className="field">
              Ground
              <span className="swatchrow">
                <input type="color" value={HEX.test(ground) ? ground : DEFAULT_GROUND}
                       onChange={(e) => setGround(e.target.value)} aria-label="Ground colour" />
                <input type="text" value={ground} spellCheck={false}
                       onChange={(e) => setGround(e.target.value.trim())} />
              </span>
            </label>
            <label className="field">
              Accent
              <span className="swatchrow">
                <input type="color" value={HEX.test(accent) ? accent : DEFAULT_ACCENT}
                       onChange={(e) => setAccent(e.target.value)} aria-label="Accent colour" />
                <input type="text" value={accent} spellCheck={false}
                       onChange={(e) => setAccent(e.target.value.trim())} />
              </span>
            </label>
            <label className="field">
              Wordmark
              <input type="text" value={wordmark} maxLength={24} placeholder={brand.name}
                     onChange={(e) => setWordmark(e.target.value)} />
              <small>Up to 24 characters. Empty uses the brand name.</small>
            </label>
            <label className="field">
              The pictures look like
              <textarea value={style} maxLength={300} rows={2}
                        placeholder="e.g. cel-shaded anime, warm palette, soft outlines"
                        onChange={(e) => setStyle(e.target.value)} />
              <small>Goes in front of every picture the director makes on this show — set backdrops, scenes, thumbnails — so they look like one show.</small>
            </label>
            <label className="field">
              The show speaks
              <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                <option value="">English</option>
                {SHOW_LANGUAGES.filter((l) => l.tag !== 'en').map((l) => <option key={l.tag} value={l.tag}>{l.label}</option>)}
              </select>
              <small>The script, the brief, the hooks and the rewrites are written in it; takes and shorts are transcribed in it; the podcast feed says it. Pick voices to match under Talent.</small>
            </label>
            {!valid && <p className="err inline">A colour must be a six-digit hex, like #ff3c41.</p>}
          </div>

          {/* Laid out like the real thumbnail, because a hex field tells you
              nothing about whether the headline will read. */}
          <figure className="kitpreview">
            <div className="kitthumb" style={{ background: HEX.test(ground) ? ground : DEFAULT_GROUND }}>
              <div className="kitkicker" style={{ color: accentText }}>
                <span className="kitbars">
                  <i style={{ background: accent }} />
                  <i style={{ background: accent }} />
                  <i style={{ background: accent }} />
                </span>
                FIELD NOTES
              </div>
              <div className="kithead" style={{ color: ink }}>
                Nobody reads the manual, and that is the whole problem
              </div>
              <div className="kitrule" style={{ background: accent }} />
              <div className="kitmark" style={{ color: dim }}>{mark.toUpperCase()}</div>
            </div>
            <figcaption>
              Headline {contrast(ink, ground).toFixed(1)}:1 · label {contrast(accentText, ground).toFixed(1)}:1
              {' '}— 4.5:1 is the readable floor.
            </figcaption>
          </figure>
        </div>
        <footer className="kit-ft">
          <button className="btn pri" disabled={!valid || !!busy} onClick={() => void save('look')}>
            {busy === 'look' ? 'Saving…' : 'Save look'}
          </button>
          <button className="btn" disabled={!!busy}
                  onClick={() => { setAccent(DEFAULT_ACCENT); setGround(DEFAULT_GROUND); setWordmark(''); }}>
            Reset to house
          </button>
          {saved === 'look' && <span className="ok">Saved — the next render uses it.</span>}
          {error?.at === 'look' && <span className="err inline">{error.message}</span>}
        </footer>
      </section>

      <section className="kit" aria-labelledby="kit-cast">
        <header className="kit-hd">
          <h3 id="kit-cast">Talent</h3>
          <p>
            A voice belongs to a <b>name</b>, not to a position in the script. Without this the
            renderer hands out voices in the order people first speak — so your host sounds like
            someone else the moment a guest opens the show. Anyone not cast keeps that default. Every show's talent, in one place, is under Talent in the nav.
          </p>
        </header>
        <div className="kit-bd">
          <div className="cast" role="table" aria-label="Cast">
            <span className="cast-h" role="columnheader">Name</span>
            <span className="cast-h" role="columnheader">Voice</span>
            <span className="cast-h" role="columnheader">Face</span>
            <span className="cast-h" role="columnheader" aria-hidden="true" />

            {Object.keys(voices).length === 0 && (
              <p className="cast-empty">
                Nobody is cast yet. {known.length > 0
                  ? 'Pick a name below to give them a fixed voice.'
                  : 'Write a script first and the names in it will appear here.'}
              </p>
            )}

            {Object.entries(voices).map(([name, id]) => (
              <Fragment key={name}>
                <span className="name" role="cell">
                  {name}
                  {!known.includes(name) && known.length > 0 && (
                    // The silent failure this screen exists to prevent, said out loud.
                    <span className="chip" title="No script uses this name">not in any script</span>
                  )}
                </span>
                <span className="row" style={{ gap: 6, minWidth: 0 }} role="cell">
                  <select aria-label={`Voice for ${name}`} value={id} style={{ minWidth: 0, flex: 1 }}
                          title={voiceLabel(id, voiceList)}
                          onChange={(e) => setVoices({ ...voices, [name]: e.target.value as VoiceId })}>
                    {!voiceList.some((v) => v.id === id) && <option value={id}>{voiceLabel(id)}</option>}
                    {groups.map(([g, list]) => (
                      <optgroup key={g} label={g}>
                        {list.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.label}{v.gender ? ` · ${v.gender}` : ''}{v.hint ? ` · ${v.hint}` : ''}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <button type="button" className="btn tiny" title="Hear a line in this voice"
                          disabled={playing !== null || !isCloudVoice(id)}
                          onClick={() => void preview(id)}>
                    {playing === id ? '…' : '▶'}
                  </button>
                </span>
                <select role="cell" aria-label={`Face for ${name}`} value={portraits[name] ?? ''}
                        title="A picture from the library, drawn as this speaker's panel"
                        onChange={(e) => {
                          const next = { ...portraits };
                          if (e.target.value) next[name] = e.target.value; else delete next[name];
                          setPortraits(next);
                        }}>
                  <option value="">drawn card</option>
                  {pictures.map((a) => <option key={a.id} value={a.id}>{a.name?.trim() || a.storage_key.split('/').pop()}</option>)}
                </select>
                <button type="button" className="btn" role="cell"
                        onClick={() => {
                          const next = { ...voices }; delete next[name]; setVoices(next);
                          const faces = { ...portraits }; delete faces[name]; setPortraits(faces);
                          const who = { ...bios }; delete who[name]; setBios(who);
                        }}>
                  Remove
                </button>
                <textarea className="cast-bio" rows={2} maxLength={600}
                          aria-label={`Who ${name} is`}
                          placeholder={`Who ${name} is — their work, their manner, what they care about. The script is written as them.`}
                          value={bios[name] ?? ''}
                          onChange={(e) => setBios({ ...bios, [name]: e.target.value })} />
              </Fragment>
            ))}

            {Object.keys(voices).length < 4 && (
              <>
                <input
                  list="brand-speakers"
                  aria-label="Cast someone as talent"
                  value={adding}
                  placeholder={known.find((k) => !(k in voices)) ?? 'Host'}
                  onChange={(e) => setAdding(e.target.value)}
                  maxLength={60}
                />
                <datalist id="brand-speakers">
                  {known.filter((k) => !(k in voices)).map((k) => <option key={k} value={k} />)}
                </datalist>
                <span className="cast-note">gets the first voice nobody else has; change it after</span>
                <span aria-hidden="true" />
                <button
                  type="button"
                  className="btn"
                  disabled={!adding.trim() || adding.trim() in voices}
                  onClick={() => {
                    // The first voice not already spoken for, so a two-hander is
                    // two different people without anyone choosing — which is the
                    // one good property of the positional default, kept.
                    const taken = new Set(Object.values(voices));
                    const free = VOICES.find((v) => !taken.has(v.id)) ?? VOICES[0];
                    void voiceList;
                    setVoices({ ...voices, [adding.trim()]: free.id });
                    setAdding('');
                  }}
                >
                  Add
                </button>
              </>
            )}
          </div>
        </div>
        <div className="kit-bd" style={{ borderTop: '1px solid var(--line)' }}>
          <h4 style={{ margin: '12px 0 4px' }}>The show's logos</h4>
          <p className="sub-dim" style={{ marginTop: 0 }}>
            The marks as pictures — the wordmark, the badge, a sponsor's. Tick the pictures that are logos;
            '+ mark' on any timeline offers them. Eight at most.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {pictures.length === 0 && <span className="sub-dim">No ready pictures in the library yet.</span>}
            {pictures.map((a) => {
              const on = logos.includes(a.id);
              return (
                <label key={a.id} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', border: `1px solid ${on ? 'var(--accent, #dc2626)' : 'var(--line)'}`, borderRadius: 6, padding: 4 }}>
                  <input type="checkbox" checked={on} disabled={!on && logos.length >= 8}
                         onChange={(e) => setLogos(e.target.checked ? [...logos, a.id] : logos.filter((x) => x !== a.id))} />
                  <Thumb asset={a} size={72} />
                  <span className="sub-dim" style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name?.trim() || a.storage_key.split('/').pop()}</span>
                </label>
              );
            })}
          </div>
          <h4 style={{ margin: '12px 0 4px' }}>The show's elements</h4>
          <p className="sub-dim" style={{ marginTop: 0 }}>
            Banners, buttons, bugs and lower thirds saved from any episode's timeline ("Save to the show's elements").
            Offered on every episode under + tag.
          </p>
          {(brand.elements ?? []).length === 0
            ? <div className="sub-dim">None yet — make one on a timeline and save it here.</div>
            : (brand.elements ?? []).map((e) => (
              <div key={e.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0' }}>
                <span className="chip">{e.props.style ?? 'banner'}</span>
                <span>{e.label}</span>
                <span className="sub-dim">{e.props.position ?? 'bottom'}{e.props.sub ? ` · ${e.props.sub}` : ''}</span>
                <span style={{ flex: 1 }} />
                <button type="button" className="btn tiny" title="Remove from the show (episodes that already placed it keep it)"
                        onClick={() => void api.removeBrandElement(brand.id, e.id).then((elements) => onSaved({ ...brand, elements })).catch((err) => setError({ at: 'cast', message: (err as Error).message }))}>×</button>
              </div>
            ))}
          <h4 style={{ margin: '12px 0 4px' }}>The podcast feed</h4>
          <p className="sub-dim" style={{ marginTop: 0 }} data-tool="podcast-feed">
            Every published episode, as audio, for Apple Podcasts, Spotify and the rest. Paste this
            address into a podcast host or app:{' '}
            <code style={{ userSelect: 'all' }}>{`${window.location.origin.replace(/^https?:\/\/app\./, 'https://api.')}/podcast/${brand.id}.xml`}</code>{' '}
            <button type="button" className="btn tiny"
                    onClick={() => { void navigator.clipboard?.writeText(`${window.location.origin.replace(/^https?:\/\/app\./, 'https://api.')}/podcast/${brand.id}.xml`); }}>copy</button>
          </p>
          <h4 style={{ margin: '12px 0 4px' }}>On the door</h4>
          <p className="sub-dim" style={{ marginTop: 0 }} data-tool="on-the-door">
            {brand.catalogue_slug ? <>The show's page at <a href={`https://rookeryos.com/c/${brand.catalogue_slug}/`} target="_blank" rel="noreferrer">rookeryos.com/c/{brand.catalogue_slug}/</a>.</> : 'The show gets a public page once it is saved.'} Its words and face there:
          </p>
          <textarea rows={2} maxLength={600} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="What the show is, in a sentence or two — shown on its page and to search engines." style={{ width: '100%', boxSizing: 'border-box' }} aria-label="The show's bio" />
          <label className="field" style={{ marginTop: 6 }}>The show's face
            <select value={avatarId} onChange={(e) => setAvatarId(e.target.value)} aria-label="The show's face">
              <option value="">{brand.avatar_key ? 'keep the current picture' : 'a picture from the library'}</option>
              {pictures.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
          <h4 style={{ margin: '12px 0 4px' }}>Where to follow the show</h4>
          <p className="sub-dim" style={{ marginTop: 0 }}>
            One handle per platform, as the platform writes it. A "Follow us" tag on the
            timeline draws them as a social bar; the platforms' logos are allowed here.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
            {SOCIAL_PLATFORMS.map((pl) => (
              <label key={pl.id} className="field">
                {pl.glyph} {pl.label}
                <input maxLength={80} value={handles[pl.id] ?? ''} placeholder={pl.id === 'website' ? 'blueheron.example' : '@handle'}
                       onChange={(e) => setHandles({ ...handles, [pl.id]: e.target.value })} />
              </label>
            ))}
          </div>
        </div>
        <footer className="kit-ft">
          <button className="btn pri" disabled={!!busy} onClick={() => void save('cast')}>
            {busy === 'cast' ? 'Saving…' : 'Save talent'}
          </button>
          {saved === 'cast' && <span className="ok">Saved — the next render uses it.</span>}
          {error?.at === 'cast' && <span className="err inline">{error.message}</span>}
          <span className="note">Five at most — a host and four guests; the five-up puts the host first.</span>
        </footer>
      </section>
    </>
  );
}
